/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import AverageOtherGoals from "../therapy/AverageOtherGoals";
import { useIonRouter } from "@ionic/react";
import useAuth from "../../hooks/useAuth";
import { getActivitySummaryByDateRange } from "../../services/dashboard";
import { useSocket } from "../../contexts/SocketContext";
import TreatmentRecapCard from "../therapy/OtherGoal";
import ActivityManager from "./ActivityManager";
import { formatDate } from "date-fns";
import { ActivityUpdateData } from "../../types/homeTypes";
import { useStorage } from "../../contexts/StorageContext";
import { Preferences } from "@capacitor/preferences";

interface GoalsSectionProps {
  profile_id?: number;
  iswellbeing?: boolean;
  onGoalUpdate?: () => void;
}

const GoalsSection: React.FC<GoalsSectionProps> = ({
  profile_id,
  onGoalUpdate,
}) => {
  const { storage, isStorageReady } = useStorage();
  const { user } = useAuth();
  const [averageGoals, setAverageGoals] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [otherTherapiesHealth, setOtherTherapiesHealth] = useState<any[]>([]);
  const [dateRange] = useState(() => {
    // Get current date in user's local timezone
    const today = new Date();
    // Calculate Monday of the current week (0 is Sunday, 1 is Monday, etc.)
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - diff);

    // Format date as yyyy-MM-dd in local time
    const formatLocalDate = (date: Date) =>
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0");

    return {
      startDate: formatLocalDate(monday),
      endDate: (() => {
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return formatLocalDate(sunday);
      })(),
    };
  });
  const router = useIonRouter();
  const { socket } = useSocket();

  useEffect(() => {
    if (socket) {
      socket.on("connect", () => {
        console.log("Socket connected:", socket.id);
      });
      socket.on("activity_refresh", () => {
        console.log("Activity refresh event received via socket");
        fetchActivityData(true);
      });
    }
  }, [socket]);

  // FIXED: Wait for storage to be ready before fetching data
  useEffect(() => {
    if (isStorageReady) {
      fetchActivityData();
    }
  }, [profile_id, dateRange, isStorageReady]);

  // Function to get current activity value from activityAdherence state
  const getCurrentActivityValue = (type: string): number => {
    return 0;
  };

  // Function to handle activity updates
  const handleActivityUpdate = async (data: ActivityUpdateData) => {
    try {
      const {
        activityType, // e.g. "Vest treatments" or "Breathing routines"
        value,
        date,
        successMessage,
        updateLocalGoals = true,
        closeModal,
      } = data;

      setLoading(true);
      const formattedDate = formatDate(date, "yyyy-MM-dd");

      // ✅ STEP 1: Update otherTherapiesHealth state first (UI immediately reflects)
      setOtherTherapiesHealth((prev) => {
        if (!prev) return prev;

        const updated = prev.map((therapy) => {
          if (
            therapy.name
              .toLowerCase()
              .includes(activityType?.replace("_treatment", "").toLowerCase())
          ) {
            const updatedTherapy = { ...therapy };

            // Update current value only if it's today
            if (formattedDate === formatDate(new Date(), "yyyy-MM-dd")) {
              updatedTherapy.value = value;
            }

            // Update the corresponding date entry inside detailedDailyValues
            updatedTherapy.detailedDailyValues =
              therapy?.detailedDailyValues?.map((day: { date: string }) => {
                if (day.date === formattedDate) {
                  return {
                    ...day,
                    value: value,
                  };
                }
                return day;
              });

            // Update dailyValues and dailyColors arrays based on the updated detailedDailyValues
            updatedTherapy.dailyValues =
              updatedTherapy.detailedDailyValues?.map((d: { value: any }) =>
                String(d.value)
              );
            updatedTherapy.dailyColors =
              updatedTherapy.detailedDailyValues?.map((d: { value: number }) =>
                d.value > 0
                  ? d.value > updatedTherapy.target
                    ? "#68C998"
                    : therapy.color
                  : "#D3D3D3"
              );
            return updatedTherapy;
          }
          return therapy;
        });

        return updated;
      });

      // ✅ STEP 2: Push to API
      await ActivityManager.recordActivityAdherence({
        profile_id: user?.profile_id as number,
        adherence_type: activityType,
        activity_value: value,
        date: formattedDate,
        activity_duration: null,
        notes: "",
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // ✅ STEP 3: Update parent/local goals if needed
      if (updateLocalGoals && onGoalUpdate) {
        onGoalUpdate();
      }

      // ✅ STEP 4: Show success message and close modal if needed
      if (successMessage) {
        console.log(successMessage);
        setActiveModal(null);
      }

      if (closeModal) {
        closeModal();
      }

      // ✅ STEP 5: Fetch fresh data to keep in sync with backend
      // await fetchActivityData();
    } catch (error) {
      console.error(`Error updating ${data.activityType}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityData = async (forceRefresh = false) => {
    setError(null);
    setLoading(true);

    try {
      // FIXED: Get the correct profile ID
      const currentProfileId = profile_id;
      if (!currentProfileId) {
        console.error("No profile ID available");
        setLoading(false);
        return;
      }
      const storedData = await Preferences.get({
        key: `otherTherapiesHealth_${currentProfileId}`,
      });
      if (storedData && storedData.value && !forceRefresh) {
        try {
          const parsedData = JSON.parse(storedData.value);
          if (parsedData.otherTherapiesHealth) {
            setOtherTherapiesHealth(parsedData.otherTherapiesHealth);
          }
          if (parsedData.averageGoals) {
            setAverageGoals(parsedData.averageGoals);
          }
        } catch (parseError) {
          console.warn(
            "Failed to parse cached data, will fetch fresh data:",
            parseError
          );
          // Continue to fetch fresh data from API below
        }
      }

      // Always fetch fresh data from API
      const response = await getActivitySummaryByDateRange(
        currentProfileId,
        dateRange.startDate,
        dateRange.endDate
      );
      await Preferences.set({
        key: `otherTherapiesHealth_${currentProfileId}`,
        value: JSON.stringify(response),
      });

      // FIXED: Update state with fresh data
      if (response.otherTherapiesHealth) {
        setOtherTherapiesHealth(response.otherTherapiesHealth);
      }
      if (response.averageGoals) {
        setAverageGoals(response.averageGoals);
      } else {
        console.log("No averageGoals in response");
      }
    } catch (err: any) {
      console.error("Error fetching activity data:", err);
      setError(err.message || "Failed to fetch activity data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="gap-4 px-2 items-center text-center">
        {/* Other Treatments */}
        <div>
          {otherTherapiesHealth.length > 0 && (
            <div className="">
              <div className="p-0.5 m-0.5">
                {otherTherapiesHealth.map(
                  (therapy, index) =>
                    therapy.target && (
                      <TreatmentRecapCard
                        currentUser={profile_id === user?.profile_id}
                        handleOnClick={() => {
                          if (therapy.name?.toLowerCase().includes("vest")) {
                            setActiveModal("vest_treatment");
                          } else if (
                            therapy.name?.toLowerCase().includes("breath")
                          ) {
                            setActiveModal("breathing_treatment");
                          }
                        }}
                        key={`therapy-${therapy.name}-${index}`}
                        therapy={therapy}
                      />
                    )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Goals */}
        <div className="card-title text-black font-bold text-left p-4">
          Goals
        </div>
        {averageGoals && averageGoals.length > 0 && (
          <div className="cursor-pointer">
            <AverageOtherGoals
              goals={averageGoals}
              profileId={profile_id}
              onClick={(id) => {
                if (profile_id === user?.profile_id) {
                  router.push(`/settings/edit-goals?from=home&goalId=${id}`);
                }
              }}
            />
          </div>
        )}
        <div className="h-4" />
      </div>

      {/* Activity Manager Modal */}
      <ActivityManager.RecordActivityModal
        activeModal={activeModal}
        onClose={() => setActiveModal(null)}
        getCurrentActivityValue={getCurrentActivityValue}
        handleActivityUpdate={async (data) => {
          // Remove closeModal from handleActivityUpdate so modal stays open
          await handleActivityUpdate({ ...data, closeModal: undefined });
        }}
      />
    </>
  );
};

export default GoalsSection;
